import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { ReactElement } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Utils } from 'shared/helpers';

interface StatusWise {
  status: string;
  count: number;
  percentage: number;
}

interface RiskRatingWise {
  count: number;
  risk_rating: string;
}

interface DashboardData {
  counts: { total: number; open: number; critical: number };
  statusWise: StatusWise[];
  riskRatingWise: RiskRatingWise[];
  remediationDeadlines: any[];
}

const DEFAULT_COLOR = '#C8C8C4';

const CHART_BOX_SX = {
  width: '100%',
  height: { xs: 240, sm: 280, md: 318, lg: 345 },
};

export const RiskTooltip = ({ active, payload }: any): ReactElement | null => {
  if (active && payload && payload.length) {
    const { name, count } = payload[0].payload;

    return (
      <div
        style={{
          backgroundColor: '#fff',
          padding: '8px 12px',
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontSize: 13,
        }}
      >
        <div>
          <strong>Risk:</strong> {name}
        </div>
        <div>
          <strong>Count :</strong> {count}
        </div>
      </div>
    );
  }

  return null;
};

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  name,
  percentage,
}: any): ReactElement => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 36;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#888"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
    >
      {`${name} ${percentage}%`}
    </text>
  );
};

interface Props {
  data: DashboardData;
  loading?: boolean;
}

const VulnerabilityCharts: React.FC<Props> = ({ data, loading }) => {
  const { statusWise = [], riskRatingWise = [] } = data || {};

  const statusChartData = statusWise.map((s: StatusWise) => ({
    name: s.status,
    value: s.count,
    percentage: s.percentage,
  }));

  const riskChartData = [...riskRatingWise]
    .sort((a, b) => {
      const order = ['Critical', 'High', 'Medium', 'Low'];
      return order.indexOf(a.risk_rating) - order.indexOf(b.risk_rating);
    })
    .map((r) => ({ name: r.risk_rating, count: r.count }));

  const maxRiskCount =
    riskChartData.length > 0
      ? Math.max(...riskChartData.map((r) => r.count), 1)
      : 1;

  const isEmpty = (arr: any[]): boolean => !arr || arr.length === 0;

  if (loading) {
    return (
      <>
        <Grid item md={6} xs={12} sx={{ display: 'flex' }}>
          <Card
            variant="outlined"
            sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <CardHeader title="Vulnerabilities by Status" />
            <CardContent sx={{ flex: 1, minHeight: 0 }}>
              <Skeleton
                variant="circular"
                width={160}
                height={160}
                sx={{ mx: 'auto' }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={6} xs={12} sx={{ display: 'flex' }}>
          <Card
            variant="outlined"
            sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <CardHeader title="Vulnerabilities by risk rating" />
            <CardContent sx={{ flex: 1, minHeight: 0 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </CardContent>
          </Card>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Grid item md={6} xs={12} sx={{ display: 'flex' }}>
        <Card
          variant="outlined"
          className="status-vulnerabilities mt-12"
          sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <CardHeader title="Vulnerabilities by Status" />
          <CardContent
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isEmpty(statusWise) ? (
              <Typography component="span" color="text.secondary">
                No vulnerability data available
              </Typography>
            ) : (
              <>
                <Box sx={CHART_BOX_SX}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius="70%"
                        dataKey="value"
                        labelLine
                        label={renderCustomLabel}
                      >
                        {statusChartData.map((entry: any, index: number) => (
                          <Cell
                            key={`${entry.name}-${index}`}
                            fill={
                              Utils.STATUS_COLOR_MAP[entry.name] ??
                              DEFAULT_COLOR
                            }
                            strokeWidth={0}
                          />
                        ))}
                      </Pie>

                      <Tooltip
                        formatter={(value, name, props) => [
                          `${props.payload.percentage}%`,
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="center"
                  gap={2}
                  mt={2}
                >
                  {statusChartData.map((entry: any, index: number) => (
                    <Box
                      key={`legend-${entry.name}-${index}`}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '2px',
                          bgcolor:
                            Utils.STATUS_COLOR_MAP[entry.name] ?? DEFAULT_COLOR,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {entry.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item md={6} xs={12} sx={{ display: 'flex' }}>
        <Card
          variant="outlined"
          className="risk-vulnerabilities mt-12"
          sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <CardHeader title="Vulnerabilities by Risk Rating" />
          <CardContent sx={{ flex: 1, minHeight: 0 }}>
            {isEmpty(riskRatingWise) ? (
              <Typography component="span" color="text.secondary">
                No vulnerability data available
              </Typography>
            ) : (
              <Box sx={CHART_BOX_SX}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={riskChartData}
                    margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                  >
                    <CartesianGrid
                      horizontal={false}
                      strokeDasharray="3 3"
                      stroke="rgba(0,0,0,0.08)"
                    />
                    <XAxis
                      type="number"
                      domain={[0, maxRiskCount + 1]}
                      allowDecimals={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                      axisLine={false}
                      tickLine={false}
                      label={{ value: 'Count', position: 'bottom', offset: -5 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 13, fill: '#333', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                      width={70}
                    />

                    <Tooltip content={<RiskTooltip />} />

                    <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={35}>
                      {riskChartData.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={
                            Utils.RISK_COLOR_MAP[entry.name] ?? DEFAULT_COLOR
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};

export default VulnerabilityCharts;
